import { useEffect, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { useSortableSensors } from "@/lib/dnd-sensors";
import { Edit2, Eye, EyeOff, GripVertical, Trash2 } from "lucide-react";
import {
  ASSET_VISIBILITY_LABEL,
  type PortalFaqItem,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useDeleteFaqItem } from "./use-delete-faq-item";
import { useReorderFaqItems } from "./use-reorder-faq-items";
import { FaqDialog } from "./faq-dialog";

// Phase 7 sub-bucket 18 — fifth consumer of vendor-dnd-kit. Optimistic
// reorder via useReorderFaqItems (Phase 6 hook, snapshot + setQueryData).

export function FaqRows({
  category,
  items,
}: {
  category: string;
  items: PortalFaqItem[];
}) {
  const reorderMutation = useReorderFaqItems();
  const [optimistic, setOptimistic] = useState<PortalFaqItem[]>(items);

  useEffect(() => {
    setOptimistic(items);
  }, [items]);

  const sensors = useSortableSensors();

  function onDragEnd(e: DragEndEvent) {
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || overId === activeId) return;

    const next = [...optimistic];
    const fromIdx = next.findIndex((r) => r.id === activeId);
    const toIdx = next.findIndex((r) => r.id === overId);
    if (fromIdx < 0 || toIdx < 0) return;

    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setOptimistic(next);
    reorderMutation.mutate({
      category,
      orderedIds: next.map((r) => r.id),
    });
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <ul className="divide-y">
        {optimistic.map((item) => (
          <FaqRowItem key={item.id} item={item} />
        ))}
      </ul>
    </DndContext>
  );
}

function FaqRowItem({ item }: { item: PortalFaqItem }) {
  const [editOpen, setEditOpen] = useState(false);
  const deleteMutation = useDeleteFaqItem();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: item.id });
  const droppable = useDroppable({ id: item.id });

  function combinedRef(el: HTMLLIElement | null) {
    setNodeRef(el);
    droppable.setNodeRef(el);
  }

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <li
      ref={combinedRef}
      style={style}
      className={cn(
        "flex items-start gap-3 p-4 transition-colors",
        isDragging && "z-50 bg-card opacity-80 shadow-md",
        droppable.isOver && !isDragging && "bg-muted/50",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 -mx-1 cursor-grab text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">
            {ASSET_VISIBILITY_LABEL[item.visibility]}
          </Badge>
          {item.active ? (
            <Badge variant="outline" className="gap-1 text-[10px]">
              <Eye className="h-3 w-3" /> Active
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 text-[10px] text-muted-foreground">
              <EyeOff className="h-3 w-3" /> Inactive
            </Badge>
          )}
        </div>
        <p className="mt-1.5 text-sm font-medium text-foreground">
          {item.question}
        </p>
        <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground line-clamp-2">
          {item.answer_md}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditOpen(true)}
          className="h-8"
        >
          <Edit2 className="mr-1 h-3.5 w-3.5" />
          Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this FAQ entry?</AlertDialogTitle>
              <AlertDialogDescription>
                This entry will no longer appear on /portal/dashboard or
                /portal/support.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate({ id: item.id })}
                disabled={deleteMutation.isPending}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {editOpen ? (
        <FaqDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          mode={{ kind: "edit", item }}
        />
      ) : null}
    </li>
  );
}
